import { applyTemplate, createProject, createTargetGroup, ensureTargetGroupLinked } from '../../api';

/**
 * Thrown when the campaign AND its first target group exist but the chosen template could not be applied to it,
 * so the UI can show the server's problems and let the user open the empty target group.
 */
export class TemplateApplyError extends Error {
  constructor(message, { project, targetGroup, cause }) {
    super(message);
    this.name = 'TemplateApplyError';
    this.project = project;
    this.targetGroup = targetGroup;
    this.cause = cause;
  }
}

/**
 * Thrown when the project itself was created but a later step failed, so the UI can
 * send the user to the existing project instead of leaving them with a silent half-state.
 */
export class PartialCreateError extends Error {
  constructor(message, { project, cause }) {
    super(message);
    this.name = 'PartialCreateError';
    this.project = project;
    this.cause = cause;
  }
}

const yesNo = (v) => (v ? 'yes' : 'no');

/** The optional contact block: only the parts the user filled in, or undefined when there is none. */
export const buildReference = ({ contactName = '', phone = '', email = '' } = {}) => {
  const reference = { contactName: contactName.trim(), phone: phone.trim(), email: email.trim() };
  const filled = Object.fromEntries(Object.entries(reference).filter(([, v]) => v !== ''));
  return Object.keys(filled).length ? filled : undefined;
};

/** The settings object the backend validates: every flag is "yes" or "no". */
export const buildSettings = ({ devices, requiresWebcam, restrictRegion, collectsPII, preventDuplicates }) => ({
  mobiles: yesNo(devices.mobiles),
  desktops: yesNo(devices.desktops),
  tablets: yesNo(devices.tablets),
  requiresWebcam: yesNo(requiresWebcam),
  restrictRegion: yesNo(restrictRegion),
  collectPersonalInfo: yesNo(collectsPII),
  preventDuplicates: yesNo(preventDuplicates),
});

/**
 * Map the dialog's form values to the project payload the backend stores.
 * No `projectStatus` is sent: the server starts every new campaign as "draft" (it is launched later from the campaign page).
 * `projectId` and the redirect links are generated by the server.
 */
export const buildProjectPayload = (values, categoryIds = []) => {
  const reference = buildReference(values);
  const surveyUrl = String(values.surveyUrl || '').trim();
  return {
    name: values.name.trim(),
    projectCategoryId: categoryIds, // the create body accepts category ids directly (backend handover section 7)
    collectsPII: yesNo(values.collectsPII), // top-level field the entity actually has (legacy UI only set the nested copy)
    projectSettings: buildSettings(values),
    ...(reference ? { projectReference: reference } : {}),
    ...(surveyUrl ? { surveyUrl } : {}), // optional: where respondents are sent ([ID] = the respondent's GUID)
  };
};

/**
 * Create project (with its categories) -> create the first target group -> make sure it is linked ->
 * (optionally) apply a template to it.
 * The backend adds a new target group to its project itself; ensureTargetGroupLinked verifies that.
 * Resolves { project, targetGroup, fromTemplate }.
 * @param {any} values the dialog's form values
 * @param {string[]} categoryIds
 * @param {string | null} [templateId] a template to apply to the first target group, or null/omitted for a blank one
 */
export async function createProjectWithTargetGroup(values, categoryIds, templateId = null) {
  const project = await createProject(buildProjectPayload(values, categoryIds));
  if (!project || !project.id) throw new Error('The server did not return the new campaign.');

  let targetGroup;
  try {
    targetGroup = await createTargetGroup(project.id, { name: 'Target group 1' });
    await ensureTargetGroupLinked(project.id, targetGroup.id);
  } catch (cause) {
    throw new PartialCreateError('The campaign was created, but setting it up did not finish.', { project, cause });
  }

  // Optional: fill the new (still empty) first target group from a template. The server checks everything first, so a
  // failure changes nothing and the target group stays blank.
  if (templateId) {
    try {
      await applyTemplate(project.id, targetGroup.id, templateId);
    } catch (cause) {
      throw new TemplateApplyError('The campaign was created, but the template could not be applied.', { project, targetGroup, cause });
    }
  }
  return { project, targetGroup, fromTemplate: Boolean(templateId) };
}
